import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { CustomBreadcrumbs } from '@/widgets/CustomBreadcrumbs';
import {
  useGetTeacherIntakesQuery,
  ITeacherIntake,
  type TeacherIntakesSortType,
} from '@/entities/TeacherIntakes';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { getTeacherCourseDetailBreadcrumbs } from '@/shared/lib/utils/getTeacherCoursesBreadcrumbs';
import { intakesColumns } from '../../config/intakesColumns';
import { intakesSortOptions } from '../../config/intakesSortOptions';
import { IntakesTableBody } from '../components/IntakesTableBody';

interface CourseDetailViewProps {
  courseId: number;
}

export const TeacherCourseDetailView = ({ courseId }: CourseDetailViewProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 8,
  });
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 500);
  const [sort, setSort] = useState<string>('createdAt:desc');
  const [expandedIntakes, setExpandedIntakes] = useState<Set<number>>(new Set());
  const prevSearchRef = useRef(debouncedSearch);

  const { data: intakesData, isLoading: isIntakesLoading } = useGetTeacherIntakesQuery({
    courseId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search: debouncedSearch || undefined,
    sort: sort !== 'default' ? (sort as TeacherIntakesSortType) : undefined,
  });

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const intakes = useMemo(() => intakesData?.intakes ?? [], [intakesData?.intakes]);
  const paginationMeta = intakesData?.pagination;
  const total = intakesData?.intakesCount ?? paginationMeta?.total ?? 0;
  const course = intakesData?.course;

  const handleSortChange = (keys: unknown) => {
    const newValue = new Set(keys as Iterable<string>).values().next().value || 'createdAt:desc';
    setSort(newValue as string);
  };

  const handleToggleIntake = useCallback((intakeId: number) => {
    setExpandedIntakes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(intakeId)) {
        newSet.delete(intakeId);
      } else {
        newSet.add(intakeId);
      }
      return newSet;
    });
  }, []);

  const tableMeta = paginationMeta
    ? {
        page: paginationMeta.page,
        pageSize: paginationMeta.pageSize,
        total: paginationMeta.total,
        pageCount: paginationMeta.pageCount,
      }
    : undefined;

  const columns = useMemo(
    () =>
      intakesColumns({
        onToggleIntake: handleToggleIntake,
        expandedIntakes,
      }),
    [expandedIntakes, handleToggleIntake],
  );

  if (isIntakesLoading) {
    return <SectionLoader />;
  }

  if (!course) {
    return null;
  }

  const breadcrumbs = getTeacherCourseDetailBreadcrumbs(course.title);

  return (
    <div className="flex flex-col gap-6 bg-white rounded-[24px] p-6 h-full min-h-0 flex-1">
      <CustomBreadcrumbs items={breadcrumbs} />

      <div className="flex items-center justify-between">
        <div className="inline-flex gap-x-2 items-end">
          <h2 className="text-[32px] font-medium">{course.title}</h2>
          <p className="text-grey-secondary leading-[220%]">
            (found {intakesData?.intakesCount ?? total} intakes)
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="w-full max-w-[530px]">
            <CustomInput
              fullWidth
              startContent={<Svg Icon={SearchIcon} />}
              placeholder="Search"
              value={search}
              onValueChange={setSearch}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <SelectSort
            sections={intakesSortOptions}
            defaultValue={sort}
            onSelectionChange={handleSortChange}
          />
        </div>

        <ContainerTable<ITeacherIntake>
          caption="Course intakes"
          data={intakes}
          meta={tableMeta}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination as OnChangeFn<PaginationState>}
          hideTopActions
          customTableBody={(rows) => (
            <IntakesTableBody
              rows={rows}
              // navigationConfig={undefined}
              // onRowClick={undefined}
              expandedIntakes={expandedIntakes}
              courseId={courseId}
            />
          )}
        />
      </div>
    </div>
  );
};
